import { useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';

// Careers admin helpers shared by the job, category, and application pages.

export const CAREERS_PORTAL_URL = (process.env.REACT_APP_CAREERS_PORTAL_URL || 'https://careers.thegraphe.com').replace(/\/+$/, '');

export const PERM = {
  view: 'careers:view',
  manageJobs: 'careers:manage_jobs',
  publishJobs: 'careers:publish_jobs',
  readApplications: 'careers:read_applications',
  manageApplications: 'careers:manage_applications',
  export: 'careers:export',
  manageCandidates: 'careers:manage_candidates',
  manageSettings: 'careers:manage_settings',
};

export const EMPLOYMENT_TYPES = ['Full Time', 'Part Time', 'Freelance', 'Internship'];

export const JOB_STATUS = {
  draft: { label: 'Draft', severity: 'secondary' },
  active: { label: 'Active', severity: 'success' },
  inactive: { label: 'Inactive', severity: 'warning' },
  closed: { label: 'Closed', severity: 'danger' },
};

// Label for the button that moves a job to each status
export const JOB_STATUS_ACTION = {
  active: { label: 'Activate', icon: 'pi pi-play' },
  inactive: { label: 'Deactivate', icon: 'pi pi-pause' },
  closed: { label: 'Close', icon: 'pi pi-lock' },
};

// Pipeline order; labels match what candidates see on the portal
export const APPLICATION_STATUS = {
  applied: { label: 'Applied', severity: 'info' },
  under_review: { label: 'Under Review', severity: 'secondary' },
  shortlisted: { label: 'Shortlisted', severity: 'warning' },
  interview: { label: 'Interview', severity: 'contrast' },
  approved: { label: 'Approved', severity: 'success' },
  hired: { label: 'Hired', severity: 'success' },
  rejected: { label: 'Rejected', severity: 'danger' },
  withdrawn: { label: 'Withdrawn', severity: 'secondary' },
};

export const APPLICATION_STATUS_KEYS = Object.keys(APPLICATION_STATUS);

// Matches the server's default notes, shown as placeholders when changing status
export const DEFAULT_CANDIDATE_NOTES = {
  applied: 'Application submitted.',
  under_review: 'Your profile is being reviewed by the hiring team.',
  shortlisted: 'You have been shortlisted for the next round.',
  interview: 'You have been selected for an interview. We will contact you with the details.',
  approved: 'Congratulations! An offer is on its way to your email.',
  hired: 'Welcome aboard! We are excited to have you join the team.',
  rejected: 'Thank you for your interest. We will not be moving forward with your application at this time.',
  withdrawn: 'You withdrew this application.',
};

export const APPLICATION_HISTORY_ACTIONS = {
  applied: 'Applied',
  status_changed: 'Status changed',
  note: 'Internal note',
  rating: 'Rating changed',
  assigned: 'Assignment changed',
};

export const EMAIL_OUTCOME = {
  pending: 'Email sending…',
  sent: 'Candidate emailed',
  skipped: 'Email not sent (email is not configured)',
  failed: 'Email failed to send',
};

export const JOB_HISTORY_ACTIONS = {
  created: 'Created',
  updated: 'Edited',
  activated: 'Activated',
  deactivated: 'Deactivated',
  closed: 'Closed',
  reopened: 'Reopened',
  duplicated: 'Created as a copy',
  deleted: 'Deleted',
};

export const JOB_FIELD_LABELS = {
  title: 'Title',
  category_id: 'Category',
  location: 'Location',
  employment_type: 'Employment type',
  is_urgent: 'Urgent',
  experience_min: 'Min experience',
  experience_max: 'Max experience',
  salary: 'Salary',
  vacancies: 'Vacancies',
  tags: 'Tags',
  description: 'Description',
  responsibilities: 'Responsibilities',
  qualifications: 'Qualifications',
  benefits: 'Benefits',
  application_deadline: 'Deadline',
  status: 'Status',
  source_job_id: 'Copied from job',
};

/** Returns request(path, { method, data, params, responseType }) for the /careers API. Resolves to response data. */
export function useCareersRequest() {
  const { accessToken } = useAuth();
  return useCallback(async (path, { method = 'get', data, params, responseType } = {}) => {
    const response = await axios({
      url: `${config.apiBASEURL}/careers${path}`,
      method,
      data,
      params,
      responseType,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  }, [accessToken]);
}

/** Returns can(permissionKey) for the signed-in user. */
export function useCareersPermissions() {
  const { permissions } = useAuth();
  return useCallback((key) => Array.isArray(permissions) && permissions.includes(key), [permissions]);
}

/** Readable message from an API error. */
export function apiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  return error?.response?.data?.message || fallback;
}

/** Field errors ({ field: message }) from a 400 response, if any. */
export function apiFieldErrors(error) {
  return error?.response?.data?.errors || {};
}

/** Local calendar date as YYYY-MM-DD (avoids the UTC shift of toISOString). */
export function toDateOnly(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD to a local Date (for date pickers). */
export function fromDateOnly(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(value) {
  if (!value) return '—';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? fromDateOnly(value) : new Date(value);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function experienceLabel(min, max) {
  if (!min && !max) return 'No experience';
  if (min === max) return `${min} year${min === 1 ? '' : 's'}`;
  return `${min}–${max} years`;
}

/** Display text for a value in job history. */
export function historyValue(field, value, categoryNames = {}) {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'category_id') return categoryNames[value] || `#${value}`;
  if (field === 'is_urgent') return value ? 'Yes' : 'No';
  if (field === 'status') return JOB_STATUS[value]?.label || value;
  if (field === 'application_deadline') return formatDate(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
}

export const portalJobUrl = (slug) => `${CAREERS_PORTAL_URL}/jobs/${slug}`;

// Same share-offsite link the portal's job page uses; LinkedIn builds the preview from the portal page
export const linkedInShareUrl = (slug) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(portalJobUrl(slug))}`;

export const applicationPath = (id) => `/Graphe/dashboard/careers/applications/${id}`;
export const candidatePath = (id) => `/Graphe/dashboard/careers/candidates/${id}`;

export const stars = (rating) => (rating ? `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}` : '—');

export function formatFileSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Save a Blob through a temporary link. */
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Open a Blob (e.g. a PDF) in a new tab. */
export function openBlob(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
}
